/* Clicking an empty square: what this tech is booked for shortly, and every
 * service they can do as a manual override. */
import * as Vue from 'vue';
import { store } from '../store.js';

var HORIZON_MIN = 30;

export const StartTurn = {
  name: 'StartTurn',
  setup: function () {
    var requested = Vue.ref(false);

    var staff = Vue.computed(function () {
      if (!store.ui.emptyPick) return null;
      return store.staffOf(store.ui.emptyPick.staffId);
    });

    var soon = Vue.computed(function () {
      if (!staff.value) return [];
      // referenced so the list re-sorts as the clock moves past a booking
      void store.minuteNow.value;
      // The note is written on the appointment, not on each service of it, so
      // it goes under the first line of a booking and not under all three.
      var said = {};
      return store.upcomingForStaff(staff.value.id, HORIZON_MIN).map(function (o) {
        var show = !!o.appt.notes && !said[o.appt.id];
        if (show) said[o.appt.id] = true;
        return {
          appt: o.appt, svc: o.svc, mapped: o.mapped, late: o.late,
          notes: show ? o.appt.notes : ''
        };
      });
    });

    var services = Vue.computed(function () {
      if (!staff.value) return [];
      return store.activeServices.value.filter(function (svc) {
        return staff.value.serviceIds.indexOf(svc.id) !== -1;
      });
    });

    /* Every service they could start, with what it would cost the book.
     *
     * The same check Next availability makes, asked of all of them at once: a tile is
     * marked before it is pressed, so the desk can pick the twenty-minute one
     * instead of finding out about the four o'clock after choosing the hour.
     * A type with no default length cannot be checked at all, and says so
     * rather than looking clear. */
    var tiles = Vue.computed(function () {
      if (!staff.value) return [];
      void store.minuteNow.value;           // re-asked as the clock closes in
      var who = staff.value;
      return services.value.map(function (svc) {
        var clash = svc.defaultMinutes
          ? store.walkInCollision(who.id, svc.id, null) : null;
        // Marked, never withheld. This sheet is the way in for work a
        // technician has agreed to stay for, which is exactly what Next availability
        // sends the desk here to ask about — so it says which ones those are
        // and then gets out of the way.
        var late = store.closingOverrun(svc.id, null);
        return { svc: svc, clash: clash, late: late,
                 title: tileTitle(who, svc, clash) +
                   (late ? '\n' + store.closingNote(svc.id, late) : '') };
      });
    });

    function tileTitle(who, svc, clash) {
      if (!svc.defaultMinutes) {
        return 'No usual length set for ' + svc.name + ', so the board cannot ' +
          'say what it would run into. Set it under Settings → Services.';
      }
      var runs = svc.name + ' usually takes ' + svc.defaultMinutes +
        ' minutes, so it would run to ';
      if (!clash) return runs + store.formatClock(store.walkInEndsAt(svc.id, null)) + '.';
      return (clash.requested
        ? clash.appt.clientName + ' asked for ' + who.name + ' at '
        : who.name + ' has ' + clash.appt.clientName + ' at ') +
        store.formatClock(clash.startsAt) + '. ' + runs +
        store.formatClock(clash.endsAt) + '.';
    }

    Vue.watch(staff, function () { requested.value = false; });

    function pickAppt(o) {
      store.quickAssign(staff.value.id, o.appt, o.svc, requested.value);
    }

    function pickService(svc) {
      store.startTurnFor(staff.value.id, svc.id, requested.value);
    }

    return {
      staff: staff, soon: soon, services: services, tiles: tiles,
      requested: requested,
      ui: store.ui,
      horizon: HORIZON_MIN,
      auth: store.auth,
      scheduled: store.schedulingOn,
      weightLabel: store.weightLabel,
      // With no book, "start a service" is the first thing offered rather than
      // the alternative to something above it.
      serviceLabel: Vue.computed(function () {
        return store.schedulingOn.value ? 'Or start a service' : 'Start a service';
      }),
      turnCredit: store.turnCredit,
      pluralTurns: store.pluralTurns,
      formatClock: store.formatClock,
      pickAppt: pickAppt,
      pickService: pickService,
      skipOneTurn: store.skipOneTurn,
      isSkipped: store.isSkipped,
      close: store.closeEmptyPick
    };
  },
  template: [
    '<div v-if="staff" class="modal-scrim" @click.self="close">',
    '  <div class="sheet sheet--wide" role="dialog" aria-modal="true">',
    '    <div class="sheet__head sheet__head--plain">',
    '      <div>',
    '        <h3>Start a turn for {{ staff.name }}</h3>',
    '        <p>{{ pluralTurns(turnCredit(staff.id)) }} so far today</p>',
    '      </div>',
    '      <label v-if="ui.requestsOn" class="switch sheet__req">',
    '        <input type="checkbox" v-model="requested" />',
    '        <span class="switch__track"><span class="switch__thumb"></span></span>',
    '        <span class="switch__label">&#9733; Request</span>',
    '      </label>',
    '    </div>',
    '',
    '    <div v-if="scheduled" class="assign__section">',
    '      <h4 class="assign__label">Booked with {{ staff.name }} in the next {{ horizon }} minutes</h4>',
    '      <p v-if="!auth.user" class="warn-line">',
    '        Sign in from the appointments pane to see today&rsquo;s book.</p>',
    '      <p v-else-if="!soon.length" class="appt-empty">',
    '        Nothing booked with {{ staff.name }} in the next {{ horizon }} minutes.</p>',
    '      <ul v-else class="pick-list">',
    '        <li v-for="o in soon" :key="o.svc.id">',
    '          <button type="button" class="pick" @click="pickAppt(o)">',
    '            <span class="pick__time">{{ formatClock(o.svc.startsAt) }}</span>',
    '            <span class="pick__main">',
    '              <span class="pick__client">{{ o.appt.clientName }}<client-note :client-id="o.appt.clientId" /></span>',
    '              <span class="pick__svc">{{ o.svc.name }}</span>',
    '            </span>',
    '            <span v-if="o.late" class="pick__tag is-live">Due</span>',
    '            <span v-else-if="!o.mapped" class="pick__tag is-later">Pick type</span>',
    '            <span v-else class="pick__letter" :style="{ background: o.mapped.color }">',
    '              {{ o.mapped.abbrev }}</span>',
    '          </button>',
    /* Whatever the booking system was told about this client, where it can
       still change what the desk does about them. */
    '          <p v-if="o.notes" class="pick__notes">{{ o.notes }}</p>',
    '        </li>',
    '      </ul>',
    '    </div>',
    '',
    '    <div class="assign__section">',
    '      <h4 class="assign__label">{{ serviceLabel }}</h4>',
    '      <p v-if="!services.length" class="warn-line">',
    '        {{ staff.name }} has no services set up &mdash; add some under Settings &rarr; Staff.</p>',
    '      <div v-else class="svc-grid">',
    '        <button v-for="t in tiles" :key="t.svc.id" type="button" class="svc-tile"',
    '          :class="{ \'svc-tile--clash\': t.clash, \'svc-tile--blocked\': t.clash && t.clash.requested }"',
    '          :style="{ \'--chip\': t.svc.color }" :title="t.title" @click="pickService(t.svc)">',
    /* What it would run into, marked before it is pressed rather than after. */
    '          <span v-if="t.clash" class="svc-tile__clash">',
    '            {{ t.clash.requested ? "\\u2605" : "!" }} {{ formatClock(t.clash.startsAt) }}</span>',
    /* Past closing. The same moon Next availability draws, and the same meaning —
       except that here it never stops the tile being pressed. */
    '          <span v-if="t.late" class="svc-tile__late"',
    '            :title="t.title">&#9790;</span>',
    '          <span class="svc-tile__letter">{{ t.svc.abbrev }}</span>',
    '          <span class="svc-tile__name">{{ t.svc.name }}</span>',
    '          <span class="svc-tile__meta">',
    '            <span class="svc-tile__mins" :class="{ \'is-unset\': !t.svc.defaultMinutes }">',
    '              {{ t.svc.defaultMinutes ? t.svc.defaultMinutes + "m" : "no time" }}</span>',
    '            <span v-if="t.svc.turnWeight !== 1" class="svc-tile__half"',
    '              :class="{ \'is-zero\': t.svc.turnWeight === 0 }">{{ weightLabel(t.svc.turnWeight) }}</span>',
    '          </span>',
    '        </button>',
    '      </div>',
    '    </div>',
    '',
    '    <div class="assign__section">',
    '      <h4 class="assign__label">Or pass</h4>',
    '      <button type="button" class="btn btn--danger-ghost btn--wide"',
    '        @click="skipOneTurn(staff.id)">Skip this turn for {{ staff.name }}</button>',
    '      <p class="setting__hint">Marks the square red and counts it against them, the same as a turn they sat out, but leaves them in the rotation for the next one. To take them out until further notice, right-click their name instead.</p>',
    '    </div>',
    '',
    '    <div class="sheet__actions">',
    '      <button type="button" class="btn btn--quiet" @click="close">Cancel</button>',
    '    </div>',
    '  </div>',
    '</div>'
  ].join('\n')
};
